import { useCallback, useEffect, useMemo } from 'react'

import Link from 'antd/es/typography/Link'
import { Flex, Text, Tabs, VStack } from 'govwell-ui'
import { TabItem } from 'govwell-ui/components/Tabs/Tabs'
import { useLocalStorage } from 'usehooks-ts'

import AIPageContainer from 'src/components/AIAssistant/AIPageContainer'
import AITrainingMaterialsTable from 'src/components/AITrainingMaterialsTable/AITrainingMaterialsTable'
import { ExternalLinkProps } from 'src/constants'
import { useGetUnansweredAIChatbotFAQsCountQuery } from 'src/fetch/aiChatbotFAQ'
import CustomAnswersTable from 'src/pages/AIAssistantTrainPage/CustomAnswersTable'
import ScrapeTemplatesTable from 'src/pages/AIAssistantTrainPage/ScrapeTemplatesTable'
import UnansweredQuestionsTable from 'src/pages/AIAssistantTrainPage/UnansweredQuestionsTable'
import { LocalStorageKey } from 'src/types'

enum AIAssistantTrainTabKey {
  CustomAnswers = 'CustomAnswers',
  UnansweredQuestions = 'UnansweredQuestions',
  UploadDocuments = 'UploadDocuments',
  ScrapeWebPages = 'ScrapeWebPages',
}

const AIAssistantTrainPage = () => {
  const [selectedTabKey, setSelectedTabKey] = useLocalStorage<AIAssistantTrainTabKey>(
    LocalStorageKey.AIAssistantTrainTabKey,
    AIAssistantTrainTabKey.CustomAnswers
  )
  const { count: unansweredCount } = useGetUnansweredAIChatbotFAQsCountQuery()

  const tabs: TabItem<AIAssistantTrainTabKey>[] = useMemo(
    () => [
      {
        label: 'Custom Answers',
        key: AIAssistantTrainTabKey.CustomAnswers,
        renderChildren: () => <CustomAnswersTable />,
      },
      {
        label: 'Unanswered Questions',
        key: AIAssistantTrainTabKey.UnansweredQuestions,
        count: unansweredCount > 0 ? unansweredCount : undefined,
        renderChildren: () => <UnansweredQuestionsTable />,
      },
      {
        label: 'Upload Documents',
        key: AIAssistantTrainTabKey.UploadDocuments,
        renderChildren: () => (
          <VStack gap={6} width="100%" align="start">
            <Text.Body>
              Upload DOCX files or searchable PDF documents to help the Community Assistant find and
              reference information when responding to users.
            </Text.Body>
            <Text.Body>
              These documents will be visible to users as citations in the Community
              Assistant&apos;s responses.
            </Text.Body>
            <Text.Body className="text-gray-600">
              Need to make a PDF searchable?{' '}
              <Link
                href="https://www.adobe.com/acrobat/hub/make-a-pdf-searchable.html"
                {...ExternalLinkProps}
              >
                Learn how with Adobe&apos;s guide
              </Link>
              .
            </Text.Body>
            <AITrainingMaterialsTable vectorStoreType="AIChat" title="Training Materials" />
          </VStack>
        ),
      },
      {
        label: 'Sync External Websites',
        key: AIAssistantTrainTabKey.ScrapeWebPages,
        renderChildren: () => <ScrapeTemplatesTable />,
      },
    ],
    [unansweredCount]
  )

  const handleSelectedKeyChange = useCallback(
    (key: AIAssistantTrainTabKey) => {
      setSelectedTabKey(key)
    },
    [setSelectedTabKey]
  )

  // Fall back to the default tab if the persisted value no longer matches a
  // known tab key (e.g. after the tab keys were renamed in a prior release).
  // This prevents an empty page when localStorage holds a stale value.
  const isSelectedTabKeyValid = tabs.some((tab) => tab.key === selectedTabKey)
  const effectiveSelectedTabKey = isSelectedTabKeyValid
    ? selectedTabKey
    : AIAssistantTrainTabKey.CustomAnswers
  useEffect(() => {
    if (!isSelectedTabKeyValid) {
      setSelectedTabKey(AIAssistantTrainTabKey.CustomAnswers)
    }
  }, [isSelectedTabKeyValid, setSelectedTabKey])

  return (
    <AIPageContainer
      title={`Train Community Assistant${unansweredCount > 0 ? ` (${unansweredCount})` : ''}`}
      customTitleDisplay="Train Community Assistant"
    >
      <Flex direction="column" gap={4} align="stretch">
        <Tabs
          onSelectedTabKeyChange={handleSelectedKeyChange}
          selectedTabKey={effectiveSelectedTabKey}
          tabs={tabs}
        />
        {tabs.find((i) => i.key === effectiveSelectedTabKey)?.renderChildren?.()}
      </Flex>
    </AIPageContainer>
  )
}

export default AIAssistantTrainPage
